import React, {createRef, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import {Button} from "primereact/button";
import {DataTable, DataTableSelectEvent, DataTableUnselectEvent} from "primereact/datatable";
import {Column} from "primereact/column";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {GlobalAttachmentDialogDefinition} from "../../../dialog/components/attachment/GlobalAttachmentDialog";
import PortalDocumentManager from "../../PortalDocumentManager";
import {pdfjs} from "react-pdf";
import PortalDialogType from "../../../dialog/PortalDialogType";
import Parameter from "../../../dialog/parameter/Parameter";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import Resizeable, {ResizeableDirection} from "../../../../shared/components/Resizeable";
import ViewerType from "../../DataType";
import DocumentSettings from "../../DocumentSettings";
import PdfViewer, {PdfViewerDefinition} from "../../../viewer/pdf/PdfViewer";
import ImageViewer, {ImageViewerDefinition} from "../../../viewer/image/ImageViewer";
import TiffViewer, {TiffViewerDefinition} from "../../../viewer/image/TiffViewer";
import HeicViewer, {HeicViewerDefinition} from "../../../viewer/image/HeicViewer";
import {ViewerMode} from "../../../viewer/ViewerMode";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import PortalContext from "../../../PortalContext";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	Attachment,
	AttachmentsDocument,
	Author,
	BaseToolbox,
	Context,
	Description,
	FileAttachment,
	SelectionAttachment,
	Settings,
	ToolboxAttachmentAttachment
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../../shared/immutable/Immutable";
import Downloader from "../../Downloader";
import PortalDialogSecurity, {Intention} from "../../../dialog/PortalDialogSecurity";
import {File} from "../../../../shared/components/FileInput";
import Translate from "../../../../shared/translation/Translate";
import {SidebarFunctionGlobals} from "./SidebarFunctions";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import LoadingViewer from "../../../viewer/LoadingViewer";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface AttachmentListProperties extends I18nGlobals, SidebarFunctionGlobals, DebugGlobals {
	context: PortalContext;
	portalDocument?: Immutable<PortalDocumentInterface>;
	visible: boolean;
}

interface AttachmentListStatus {
	previewHeight: string;
	previewData?: Blob;
}

export class AttachmentListDefinition extends PortalPureComponent<AttachmentListProperties, AttachmentListStatus> {
	/**
	 * Attachment list component constructor
	 */
	constructor(props: Readonly<AttachmentListProperties>) {
		super(props);

		this.state = {
			previewHeight: "25%"
		};

		this.renderAuthor = this.renderAuthor.bind(this);
		this.renderEmptyMessage = this.renderEmptyMessage.bind(this);
		this.downloadAttachments = this.downloadAttachments.bind(this);
		this.extractAttachments = this.extractAttachments.bind(this);
		this.deleteAttachments = this.deleteAttachments.bind(this);
		this.onSelect = this.onSelect.bind(this);
		this.onUnselect = this.onUnselect.bind(this);
		this.onSelectAll = this.onSelectAll.bind(this);
		this.onUnselectAll = this.onUnselectAll.bind(this);
	}

	/**
	 * called on select all
	 */
	private onSelectAll(event: DataTableSelectEvent): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.attachmentSelection = [];

		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);
		for (let attachment of attachments) {
			if (!FunctionHelper.exists(attachment.objectKey)) {
				continue;
			}

			documentSettings.attachmentSelection.push(attachment.objectKey!);
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * called on unselect all
	 */
	private onUnselectAll(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.attachmentSelection = [];

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * called on unselecting an attachment
	 */
	private onUnselect(event: DataTableUnselectEvent): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let attachmentEntry: Attachment = event.data;
		let selection: Array<string> = [];

		if (event.type !== "row") {
			for (let entry of documentSettings.attachmentSelection) {
				if (entry === attachmentEntry.objectKey) {
					continue;
				}

				selection.push(entry);
			}
		} else {
			selection.push(FunctionHelper.applyValue(attachmentEntry.objectKey, Attachment.getObjectKeyDefault()));
		}

		documentSettings.attachmentSelection = selection;

		event.originalEvent.stopPropagation();
		event.originalEvent.preventDefault();
		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * called on selecting an attachment
	 */
	private onSelect(event: DataTableSelectEvent): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let attachmentEntry: Attachment = event.data;
		let selection: Array<string> = [];

		if (event.type !== "row") {
			for (let entry of documentSettings.attachmentSelection) {
				if (entry === attachmentEntry.objectKey) {
					continue;
				}

				selection.push(entry);
			}
		}

		selection.push(FunctionHelper.applyValue(attachmentEntry.objectKey, Attachment.getObjectKeyDefault()));
		documentSettings.attachmentSelection = selection;

		event.originalEvent.stopPropagation();
		event.originalEvent.preventDefault();
		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private checkPasswords(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError())
		) {
			return true;
		}

		return false;
	}

	private async deleteAttachments(): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.deleteAttachments();
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		let deleteParameter: Parameter<ToolboxAttachmentAttachment> = DialogDefault.getParameter(
			PortalDialogType.ATTACHMENT_DELETE, [portalDocument]
		)!;
		deleteParameter.data.remove!.selection = [];

		for (let attachment of attachments) {
			if (documentSettings.attachmentSelection.indexOf(
				FunctionHelper.applyValue(attachment.objectKey, Attachment.getObjectKeyDefault())
			) === -1) {
				continue;
			}

			let selection: SelectionAttachment = SelectionAttachment.fromJson({
				fileMask: attachment.name,
				context: attachment.page! > 0 ? Context.Page : Context.Document
			} as SelectionAttachment);

			if (attachment.page! > 0) {
				selection.pages = attachment.page!.toString();
			}

			deleteParameter.data.remove!.selection.push(selection);
		}

		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		DialogDefault.setParameter(PortalDialogType.ATTACHMENT_DELETE, deleteParameter, portalDocuments);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			attachment: deleteParameter.data
		});

		for (let portalDocument of portalDocuments) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: WebServiceTypes.TOOLBOX,
					document: portalDocument,
					operation: [toolboxOperation],
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.deleteAttachments();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.attachment.delete", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		documentSettings.attachmentSelection = [];

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private async extractAttachments(): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.extractAttachments();
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		let extractParameter: Parameter<ToolboxAttachmentAttachment> = DialogDefault.getParameter(
			PortalDialogType.ATTACHMENT_EXTRACT, [portalDocument]
		);
		extractParameter.data.extract!.selection = [];

		for (let attachment of attachments) {
			if (documentSettings.attachmentSelection.indexOf(
				FunctionHelper.applyValue(attachment.objectKey, Attachment.getObjectKeyDefault())
			) === -1) {
				continue;
			}

			let selection: SelectionAttachment = SelectionAttachment.fromJson({
				fileMask: attachment.name,
				context: attachment.page! > 0 ? Context.Page : Context.Document
			} as SelectionAttachment);

			if (attachment.page! > 0) {
				selection.pages = attachment.page!.toString();
			}

			extractParameter.data.extract!.selection.push(selection);
		}

		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		DialogDefault.setParameter(PortalDialogType.ATTACHMENT_EXTRACT, extractParameter, portalDocuments);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			attachment: extractParameter.data
		});

		for (let portalDocument of portalDocuments) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: WebServiceTypes.TOOLBOX,
					document: portalDocument,
					operation: [toolboxOperation],
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.extractAttachments();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.attachment.extract", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}
	}

	private async downloadAttachments(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		let pdfDocument: pdfjs.PDFDocumentProxy;

		try {
			pdfDocument = await portalDocument.loadViewerData();
		} catch (e: any) {
			return;
		}

		let entries: Array<{ name: string, extension?: string, data: any }> = [];

		for (let attachment of attachments) {
			if (documentSettings.attachmentSelection.indexOf(
				FunctionHelper.applyValue(attachment.objectKey, Attachment.getObjectKeyDefault())
			) === -1) {
				continue;
			}

			let attachmentContent: BlobPart | undefined;

			if (FunctionHelper.exists(attachment.page) && attachment.page !== 0) {
				let pageProxy: pdfjs.PDFPageProxy = await pdfDocument.getPage(attachment.page!);
				let annotations: Array<any> = await pageProxy.getAnnotations();

				for (let annotation of annotations) {
					if (annotation.subtype === 'FileAttachment' && annotation.file.filename === attachment.name) {
						attachmentContent = annotation.file.content;
						break;
					}
				}
			} else {
				let globalAttachments: any = await pdfDocument.getAttachments();

				for (let key in globalAttachments) {
					if (globalAttachments.hasOwnProperty(key) && globalAttachments[key].filename === attachment.name) {
						attachmentContent = globalAttachments[key].content;
						break;
					}
				}
			}

			if (!FunctionHelper.exists(attachmentContent)) {
				return;
			}

			let fileBlob: Blob = new Blob([attachmentContent!], {type: attachment.mimeType});

			entries.push({
				name: attachment.name!,
				data: fileBlob
			});
		}

		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);

		if (entries.length === 1) {
			for (let entry of entries) {
				await downloader.download(entry.data, entry.name);
			}
		} else {
			await downloader.downloadDocuments(entries);
		}
	}

	private async dropFiles(fileList: FileList): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let parameter: Parameter<ToolboxAttachmentAttachment> = DialogDefault.getParameter(
			PortalDialogType.ATTACHMENT_ADD_GLOBAL, documentManager.getSelection()
		);
		parameter.data!.add!.file = [];

		let files: Array<File> = [];
		for (let file of fileList) {
			files.push(
				await new Promise(function (resolve) {
					let fileReader: FileReader = new FileReader();

					fileReader.onload = function () {
						if (typeof fileReader.result !== "string") {
							return;
						}

						let filedata: string = fileReader.result.substring(fileReader.result.indexOf(',') + 1);
						let filetype: string = file.type;
						let filename: string = file.name;
						filename = (filename.indexOf('\\') === -1) ?
							filename : filename.substring(filename.lastIndexOf('\\') + 1);

						resolve({
							name: filename,
							mimetype: filetype,
							value: filedata
						} as File);
					};

					fileReader.readAsDataURL(file);
				})
			);
		}

		for (let file of files) {
			let operationFileAttachment: FileAttachment = FileAttachment.fromJson({
				fileName: file.name,
				mimeType: file.mimetype,
				data: {
					value: file.value
				}
			} as FileAttachment);
			parameter.data!.add!.file!.push(operationFileAttachment);
		}

		await this.addGlobalAttachments(parameter);
	}

	private async addGlobalAttachments(parameter: Parameter<ToolboxAttachmentAttachment>) {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.addGlobalAttachments(parameter);
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			attachment: parameter.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];

		try {
			await this.props.context.session.executeRequest({
				webServiceType: WebServiceTypes.TOOLBOX,
				document: portalDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.addGlobalAttachments(parameter);
			}
			return;
		}

		let message: Message = new Message("success.toolbox.attachment.add", MessageType.SUCCESS);
		message.context = {filename: '"' + portalDocument.getFileName() + '" '};
		this.props.context.getPage().addMessage(message);
	}

	/**
	 * Message to render if the list is empty
	 */
	private renderEmptyMessage(attachments: Array<Attachment>): ReactNode {
		if (attachments.length !== 0) {
			return null;
		}

		return (
			<p className="no-results">
				<Translate {...this.props} data="attachment.empty" namespace="sidebar"/>
			</p>
		);
	}

	/**
	 * Message to render if the preview is empty
	 */
	private renderEmptyPreviewMessage(): ReactNode {
		return (
			<p className="no-results">
				<Translate {...this.props} data="attachment.preview.empty" namespace="sidebar"/>
			</p>
		);
	}

	/**
	 * render the author for an attachment in the list
	 */
	private renderAuthor(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let authors: Array<Author> = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.description?.author, Description.getAuthorDefault()
		);
		let result: string = "";

		for (let author of authors) {
			result += author.name + '; ';
		}

		return <span>{result}</span>;
	}

	/**
	 * render the preview for an attachment list item
	 */
	private renderPreview(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return this.renderEmptyPreviewMessage();
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		)
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.ATTACHMENT}/>;
		}

		if (documentSettings.attachmentSelection.length === 1) {
			let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
				documentSettings.attachmentSelection[0]
			);

			if (FunctionHelper.exists(selectedAttachment)) {
				let viewerType: ViewerType = portalDocument.getViewerType(selectedAttachment);

				switch (viewerType) {
					case ViewerType.PDF:
						return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props}
										  attachmentKey={selectedAttachment!.objectKey}
										  viewerMode={ViewerMode.ATTACHMENT}/>;
					case ViewerType.IMAGE:
						return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props}
											attachmentKey={selectedAttachment!.objectKey}
											viewerMode={ViewerMode.ATTACHMENT}/>;
					case ViewerType.TIFF:
						return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props}
										   attachmentKey={selectedAttachment!.objectKey}
										   viewerMode={ViewerMode.ATTACHMENT}/>;
					case ViewerType.HEIC:
						return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props}
										   attachmentKey={selectedAttachment!.objectKey}
										   viewerMode={ViewerMode.ATTACHMENT}/>;
				}
			}
		}

		return this.renderEmptyPreviewMessage();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let attachmentList: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let attachments: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);

		let attachmentSelection: Array<Attachment> = [];
		for (let attachment of attachments) {
			if (documentSettings.attachmentSelection.indexOf(
				FunctionHelper.applyValue(attachment.objectKey, Attachment.getObjectKeyDefault())
			) === -1) {
				continue;
			}

			attachmentSelection.push(attachment);
		}

		return (
			<div className="body" style={this.props.visible ? {} : {display: "none"}}
				 data-help="global.sidebar.attachments"
				 data-position="right">
				<div className="toolbar">
					<Button className={documentSettings.attachmentPreview ? "active" : ""}
							icon="icon icon-eye"
							tooltip={Translate.value("attachment.preview.toggle", {ns: "sidebar"})}
							onClick={() => {
								if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
									return null;
								}

								let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
									this.props.portalDocument!.get().id!
								);
								let documentSettings: DocumentSettings = portalDocument.getSettings();
								documentSettings.attachmentPreview = !documentSettings.attachmentPreview;

								this.props.context.documentManager.updateGlobalDocumentList();
							}}/>
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.editAttachment
						), false) &&
						<div className="separator"/>
					}
					{(!this.props.documentChecks || GlobalAttachmentDialogDefinition.isEnabled(this.props.context.info)) &&
						<Button icon="icon icon-add"
								disabled={this.props.documentChecks && !GlobalAttachmentDialogDefinition.isActive(
									selection, this.props.context.info, this.props.signatureEditProfile
								)}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({
											showGlobalAttachmentDialog: true,
											globalAttachmentParameter: undefined
										})
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								tooltip={Translate.value("attachment.add", {ns: "sidebar"})}/>
					}
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.editAttachment
						), false) &&
						<Button icon="icon icon-delete small"
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										await this.deleteAttachments()
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								disabled={(() => {
									if (portalDocument.isLocked()) {
										return true;
									}

									if (!PortalDialogSecurity.checkRestrictions(
										PortalDialogType.ATTACHMENT_DELETE, this.props.signatureEditProfile,
										Intention.editingContent, portalDocument
									)) {
										return true;
									}

									return documentSettings.attachmentSelection.length < 1
								})()}
								tooltip={Translate.value("attachment.remove", {ns: "sidebar"})}/>
					}
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.editAttachment
						), false) &&
						<Button icon="icon icon-export small"
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										await this.extractAttachments()
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								disabled={(() => {
									if (portalDocument.isLocked()) {
										return true;
									}

									if (!PortalDialogSecurity.checkRestrictions(
										PortalDialogType.ATTACHMENT_EXTRACT, this.props.signatureEditProfile,
										Intention.reading, portalDocument
									)) {
										return true;
									}

									return documentSettings.attachmentSelection.length < 1
								})()}
								tooltip={Translate.value("attachment.export", {ns: "sidebar"})}/>
					}
					{
						FunctionHelper.applyValue((
							this.props.context.info.webserviceStatus?.webservices?.toolbox?.enabled &&
							this.props.context.info.functionsStatus?.editAttachment
						), false) &&
						<Button icon="icon icon-download small"
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										await this.downloadAttachments();
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								disabled={
									documentSettings.attachmentSelection.length < 1 || portalDocument.isLocked()
								}
								tooltip={Translate.value("attachment.download", {ns: "sidebar"})}/>
					}
				</div>
				<div className="attachment-list" ref={attachmentList}
					 onDrop={(event) => this.dropFiles(event.dataTransfer.files)}>
					{this.renderEmptyMessage(attachments)}
					{attachments.length > 0 &&
						<DataTable value={attachments} selectionMode="multiple" selection={attachmentSelection}
								   onAllRowsUnselect={this.onUnselectAll} onAllRowsSelect={this.onSelectAll}
								   header={null} onRowUnselect={this.onUnselect} onRowSelect={this.onSelect}
								   dataKey="objectKey" responsiveLayout={"scroll"}>
							<Column className={"column-select"} selectionMode="multiple"/>
							<Column field="name" className="column-filename"
									header={
										<Translate {...this.props} data="attachment.filename" namespace="sidebar"/>
									}
									body={(item: Attachment) => {
										return <span>{item.name}</span>;
									}}/>
							<Column field="description" className="column-description"
									header={
										<Translate {...this.props} data="attachment.description" namespace="sidebar"/>
									}
									body={(item: Attachment) => {
										return <span>{item.description}</span>;
									}}/>
							<Column className="column-author"
									header={
										<Translate {...this.props} data="attachment.author" namespace="sidebar"/>
									}
									body={this.renderAuthor}/>
							<Column className="column-position"
									header={
										<Translate {...this.props} data="attachment.position" namespace="sidebar"/>
									}
									body={(item: Attachment) => {
										return item.page !== 0 ?
											<Translate {...this.props} data="attachment.page" namespace="sidebar"
													   values={{page: item.page}}/> :
											<Translate {...this.props} data="attachment.global" namespace="sidebar"/>;
									}}/>
						</DataTable>
					}
					{documentSettings.attachmentPreview &&
						<div className="attachment-preview" style={{height: this.state.previewHeight}}>
							<Resizeable direction={ResizeableDirection.VERTICAL} container={attachmentList}
										invert={true}
										resizeCallback={(options: { height: string }) => {
											this.setState({
												previewHeight: options.height
											});
										}}/>
							{this.renderPreview()}
						</div>
					}
				</div>
				<div className="toolbar inverted"/>
			</div>
		);
	}
}

const AttachmentList = React.memo(AttachmentListDefinition,
	(prevProps: Readonly<AttachmentListProperties>, nextProps: Readonly<AttachmentListProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.visible !== prevProps.visible) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default AttachmentList;